import React, { useState, useEffect, useCallback } from 'react';
import { PeriodoResponse } from '../types/periodo.types';
import { AñoEscolar } from '../types/usuario.types';
import { periodoService } from '../services/periodoService';
import { catalogoService } from '../services/usuarioService';
import PeriodoFormPage from './PeriodoFormPage';
import { fmtAnioEscolar } from 'utils/anioEscolar';

// ============================================================
// PeriodosPage – Lista de periodos con CRUD completo
// ============================================================

type Vista = 'list' | 'new' | 'edit';

// ── Íconos compactos ────────────────────────────────────────
const Ico = ({ d, cls = 'w-4 h-4' }: { d: string; cls?: string }) => (
  <svg viewBox="0 0 20 20" fill="currentColor" className={cls}><path d={d} /></svg>
);
const D_PLUS   = 'M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z';
const D_SEARCH = 'M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z';
const D_EDIT   = 'M5.433 13.917l1.262-3.155A4 4 0 0 1 7.58 9.42l6.92-6.918a2.121 2.121 0 0 1 3 3l-6.92 6.918c-.383.383-.84.685-1.343.886l-3.154 1.262a.5.5 0 0 1-.65-.65ZM3.5 5.75c0-.69.56-1.25 1.25-1.25H10A.75.75 0 0 0 10 3H4.75A2.75 2.75 0 0 0 2 5.75v9.5A2.75 2.75 0 0 0 4.75 18h9.5A2.75 2.75 0 0 0 17 15.25V10a.75.75 0 0 0-1.5 0v5.25c0 .69-.56 1.25-1.25 1.25h-9.5c-.69 0-1.25-.56-1.25-1.25v-9.5Z';
const D_DEL    = 'M8.75 1A2.75 2.75 0 0 0 6 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 1 0 .23 1.482l.149-.022.841 10.518A2.75 2.75 0 0 0 7.596 19h4.807a2.75 2.75 0 0 0 2.742-2.53l.841-10.52.149.023a.75.75 0 0 0 .23-1.482A41.03 41.03 0 0 0 14 4.193V3.75A2.75 2.75 0 0 0 11.25 1h-2.5ZM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4ZM8.58 7.72a.75.75 0 0 0-1.5.06l.3 7.5a.75.75 0 1 0 1.5-.06l-.3-7.5Zm4.34.06a.75.75 0 1 0-1.5-.06l-.3 7.5a.75.75 0 1 0 1.5.06l.3-7.5Z';

const SpinnerIcon = () => (
  <svg className="animate-spin w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
  </svg>
);

const BTN_COLOR: Record<string, string> = {
  blue: 'hover:text-blue-600 hover:bg-blue-50',
  red:  'hover:text-red-500  hover:bg-red-50',
};

const ActionBtn: React.FC<{ title: string; color: string; onClick: () => void; children: React.ReactNode }> =
  ({ title, color, onClick, children }) => (
    <button onClick={onClick} title={title}
      className={`p-1.5 rounded-lg text-slate-400 transition-colors ${BTN_COLOR[color]}`}>
      {children}
    </button>
  );

// ── Componente principal ────────────────────────────────────
const PeriodosPage: React.FC = () => {
  const [vista, setVista]   = useState<Vista>('list');
  const [editId, setEditId] = useState<number | null>(null);

  const [periodos, setPeriodos]   = useState<PeriodoResponse[]>([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState<string | null>(null);

  // Catálogo
  const [añosEscolares, setAñosEscolares] = useState<AñoEscolar[]>([]);
  const [anioFilter, setAnioFilter]       = useState('');

  // Filtros
  const [search, setSearch] = useState('');

  // Eliminar
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  // ── Cargar catálogo de años al montar ─────────────────────
  useEffect(() => {
    catalogoService.getAñosEscolares().then(setAñosEscolares).catch(() => {});
  }, []);

  // ── Cargar periodos (por año o todos) ─────────────────────
  const loadPeriodos = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = anioFilter
        ? await periodoService.listarPorAnioEscolar(Number(anioFilter))
        : await periodoService.listar();
      setPeriodos(data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al cargar periodos');
    } finally { setLoading(false); }
  }, [anioFilter]);

  useEffect(() => { loadPeriodos(); }, [loadPeriodos]);

  const handleSuccess = () => { setVista('list'); loadPeriodos(); };

  const confirmDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await periodoService.eliminar(deleteId);
      setDeleteId(null);
      loadPeriodos();
    } catch { /* httpClient ya maneja el error */ }
    finally { setDeleting(false); }
  };

  // ── Filtrado del lado cliente ─────────────────────────────
  const periodosFiltrados = periodos.filter(p => {
    if (search === '') return true;
    const s = search.toLowerCase();
    return (
      p.nombre.toLowerCase().includes(s) ||
      String(p.anio).includes(s) ||
      String(p.numero).includes(s)
    );
  });

  // ── Formulario ────────────────────────────────────────────
  if (vista === 'new' || vista === 'edit') {
    return (
      <PeriodoFormPage
        idPeriodo={vista === 'edit' ? editId! : undefined}
        onSuccess={handleSuccess}
        onCancel={() => setVista('list')}
      />
    );
  }

  // ── Vista lista ───────────────────────────────────────────
  return (
    <div className="space-y-5">

      {/* Cabecera */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Gestión de Periodos</h1>
          <p className="text-slate-400 text-sm mt-0.5">Administra los periodos académicos del año escolar</p>
        </div>
        <button onClick={() => setVista('new')}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold
                     bg-blue-700 hover:bg-blue-800 text-white transition-colors shadow-sm">
          <Ico d={D_PLUS} />
          Nuevo Periodo
        </button>
      </div>

      {/* Filtros */}
      <div className="flex gap-3">
        <div className="relative flex-1 max-w-sm">
          <span className="absolute inset-y-0 left-3 flex items-center text-slate-400">
            <Ico d={D_SEARCH} />
          </span>
          <input
            type="text"
            placeholder="Buscar periodos..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 bg-white text-sm
                       placeholder-slate-300 outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <select
          value={anioFilter}
          onChange={e => setAnioFilter(e.target.value)}
          className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-700
                     outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">Todos los años</option>
          {añosEscolares.map(a => (
            <option key={a.idAnioEscolar} value={a.idAnioEscolar}>{fmtAnioEscolar(a)}</option>
          ))}
        </select>
      </div>

      {/* Contenido */}
      {loading ? (
        <div className="flex justify-center py-20"><SpinnerIcon /></div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-600 text-sm">{error}</div>
      ) : periodosFiltrados.length === 0 ? (
        <div className="text-center py-16">
          <div className="mx-auto w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-8 h-8 text-slate-400">
              <path strokeLinecap="round" strokeLinejoin="round"
                d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
            </svg>
          </div>
          <p className="text-slate-500 font-semibold">Sin periodos registrados</p>
          <p className="text-slate-400 text-sm mt-1">Crea el primer periodo para comenzar</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-100 overflow-hidden shadow-sm">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">Nombre</th>
                <th className="px-6 py-3 text-center text-xs font-bold text-slate-500 uppercase tracking-wide">N°</th>
                <th className="px-6 py-3 text-center text-xs font-bold text-slate-500 uppercase tracking-wide">Año</th>
                <th className="px-6 py-3 text-center text-xs font-bold text-slate-500 uppercase tracking-wide">Fecha Inicio</th>
                <th className="px-6 py-3 text-center text-xs font-bold text-slate-500 uppercase tracking-wide">Fecha Fin</th>
                <th className="px-6 py-3 text-center text-xs font-bold text-slate-500 uppercase tracking-wide">Estado</th>
                <th className="px-6 py-3 text-right  text-xs font-bold text-slate-500 uppercase tracking-wide">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {periodosFiltrados.map(p => (
                <tr key={p.idPeriodo} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4 text-sm font-semibold text-slate-800">{p.nombre}</td>
                  <td className="px-6 py-4 text-sm text-slate-600 text-center">{p.numero}</td>
                  <td className="px-6 py-4 text-sm text-slate-600 text-center">{p.anio}</td>
                  <td className="px-6 py-4 text-sm text-slate-600 text-center">{p.fechaInicio}</td>
                  <td className="px-6 py-4 text-sm text-slate-600 text-center">{p.fechaFin}</td>
                  <td className="px-6 py-4 text-center">
                    <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold
                      ${p.activo
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100  text-gray-600'
                      }`}>
                      {p.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex gap-1 justify-end">
                      <ActionBtn title="Editar" color="blue" onClick={() => { setEditId(p.idPeriodo); setVista('edit'); }}>
                        <Ico d={D_EDIT} />
                      </ActionBtn>
                      <ActionBtn title="Eliminar" color="red" onClick={() => setDeleteId(p.idPeriodo)}>
                        <Ico d={D_DEL} />
                      </ActionBtn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal confirmar eliminación */}
      {deleteId !== null && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full mx-4">
            <h3 className="font-bold text-lg text-slate-800 mb-2">¿Eliminar periodo?</h3>
            <p className="text-slate-500 text-sm mb-5">Esta acción no se puede deshacer. El periodo será eliminado permanentemente.</p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setDeleteId(null)}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors">
                Cancelar
              </button>
              <button onClick={confirmDelete} disabled={deleting}
                className="px-4 py-2 rounded-lg text-sm font-bold bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-60">
                {deleting ? <SpinnerIcon /> : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PeriodosPage;
